import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Package, ChevronDown, ChevronUp, Sparkles, AlertCircle } from "lucide-react";

/**
 * PalletManifest — shows "What's inside this pallet" on the product page for
 * closeout/overstock pallets. Renders the structured items list parsed from
 * the supplier manifest, with total MSRP value and savings vs. sale price.
 */
export default function PalletManifest({ productId }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    api
      .get(`/products/${productId}/manifest`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId]);

  if (loading || !data || !data.items?.length) return null;

  const { items, total_msrp, count_lines, your_price, savings_dollars, savings_percent, per_item_msrp, pallet_number } = data;
  const PREVIEW = 6;
  const shown = expanded ? items : items.slice(0, PREVIEW);
  const hasMore = items.length > PREVIEW;

  return (
    <section className="mt-12 border-2 border-[#0A1C40]" data-testid="pallet-manifest">
      {/* Header — MSRP vs. your price */}
      <div className="bg-[#0A1C40] text-white p-6 lg:p-8">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-[#FFC220]">
          <Package className="w-3 h-3" /> Inside this pallet
        </div>
        {pallet_number && (
          <div
            data-testid="pallet-number-badge"
            className="font-display font-black text-2xl lg:text-3xl tracking-tighter mt-3 inline-block bg-[#FFC220] text-[#0A1C40] px-3 py-1"
          >
            {pallet_number}
          </div>
        )}
        <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-2">
          {count_lines} brand-new items.
        </h2>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div data-testid="manifest-stat-msrp">
            <div className="text-[10px] tracking-widest uppercase text-white/60 font-bold">Total MSRP value</div>
            <div className="font-display font-black text-2xl lg:text-3xl mt-1 line-through text-white/70">
              {fmtMoney(total_msrp)}
            </div>
            {per_item_msrp > 0 && (
              <div className="text-[11px] tracking-wider uppercase text-white/50 font-bold mt-1" data-testid="manifest-stat-per-item">
                ≈ {fmtMoney(per_item_msrp)} / item
              </div>
            )}
          </div>
          <div data-testid="manifest-stat-yourprice">
            <div className="text-[10px] tracking-widest uppercase text-white/60 font-bold">Your price</div>
            <div className="font-display font-black text-2xl lg:text-3xl mt-1 text-[#FFC220]">
              {fmtMoney(your_price)}
            </div>
          </div>
          {savings_dollars > 0 && (
            <>
              <div data-testid="manifest-stat-savings">
                <div className="text-[10px] tracking-widest uppercase text-white/60 font-bold">You save</div>
                <div className="font-display font-black text-2xl lg:text-3xl mt-1 text-green-400">
                  {fmtMoney(savings_dollars)}
                </div>
              </div>
              <div data-testid="manifest-stat-percent">
                <div className="text-[10px] tracking-widest uppercase text-white/60 font-bold">Discount</div>
                <div className="font-display font-black text-2xl lg:text-3xl mt-1 text-green-400 inline-flex items-center gap-1">
                  <Sparkles className="w-5 h-5" /> {savings_percent}% off
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Item table */}
      <div className="bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="manifest-items-table">
          <thead className="border-b border-gray-200">
            <tr className="text-left">
              <th className="p-3 text-[10px] tracking-widest uppercase font-bold text-gray-500">Qty</th>
              <th className="p-3 text-[10px] tracking-widest uppercase font-bold text-gray-500">Item</th>
              <th className="p-3 text-[10px] tracking-widest uppercase font-bold text-gray-500 text-right">MSRP</th>
              <th className="p-3 text-[10px] tracking-widest uppercase font-bold text-gray-500 text-right hidden sm:table-cell">Total MSRP</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((it) => (
              <tr key={it.id} className="border-b border-gray-100 last:border-b-0" data-testid={`manifest-row-${it.id}`}>
                <td className="p-3 font-bold whitespace-nowrap">{it.quantity}×</td>
                <td className="p-3">
                  <div className={it.needs_review ? "text-gray-400 italic" : ""}>{it.name}</div>
                  {it.sku && <div className="text-xs text-gray-400 font-mono">{it.sku}</div>}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {it.msrp ? fmtMoney(it.msrp) : <span className="text-gray-300">—</span>}
                </td>
                <td className="p-3 text-right whitespace-nowrap font-semibold hidden sm:table-cell">
                  {it.msrp_total ? fmtMoney(it.msrp_total) : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
          {hasMore && (
            <tfoot>
              <tr>
                <td colSpan={4} className="p-0">
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="w-full py-3 bg-[#FAFAFA] hover:bg-gray-100 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
                    data-testid="manifest-toggle-all"
                  >
                    {expanded ? (
                      <><ChevronUp className="w-4 h-4" /> Show first {PREVIEW} only</>
                    ) : (
                      <><ChevronDown className="w-4 h-4" /> Show all {items.length} items</>
                    )}
                  </button>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Footer disclaimer */}
      <div className="bg-[#FAFAFA] p-4 text-xs text-gray-500 border-t border-gray-200 flex items-start gap-2">
        <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <div>
          Manifest is the load's content list. Specific items, brands, or quantities
          may occasionally vary by a small amount. All items are brand-new (no returns,
          no damages). Total MSRP is calculated from manufacturer suggested retail prices.
        </div>
      </div>
    </section>
  );
}
